// Phone-entry helpers shared by the calendar new-patient quick-create and the
// "New message" phone-entry mode.

// Default country-code prefix shown as PLACEHOLDER text in an empty phone field.
//
// It used to be prefilled as the field's initial value, which is how a doubled country code
// reached the backend: staff pasting a number that already carried "65" got "+65" + "6580282555"
// = "+656580282555". That is not a WhatsApp account, so WhatsApp accepted every send to it and
// delivered none — see validatePhoneInput below, which now refuses the shape outright.
export const DEFAULT_PHONE_PREFIX = "+65";

// Mirrors the backend's whatsapp.defaultCountryCode / nationalNumberLength
// (healo_backend/config/config.yaml) and internal/whatsappphone/phone.go. Kept in step with the
// Go table test in healo_backend/tests/whatsapp_phone_test.go — the two must agree.
const DEFAULT_COUNTRY_CODE = "65";
const NATIONAL_NUMBER_LENGTH = 8;
const MIN_PHONE_DIGITS = 8;
const MAX_PHONE_DIGITS = 15; // E.164 cap

// Digits only, minus a leading "00" international-access prefix — the same normalization the
// backend applies before deriving a chat id.
function phoneDigits(value) {
  const digits = String(value || "").replace(/\D/g, "");
  return digits.startsWith("00") ? digits.slice(2) : digits;
}

// Mask a phone input to a single leading "+" followed by digits only (E.164 caps
// at 15 digits). An empty input stays empty rather than becoming a lone "+".
export function maskPhoneInput(raw) {
  const digits = String(raw || "").replace(/\D/g, "").slice(0, MAX_PHONE_DIGITS);
  return digits ? "+" + digits : "";
}

// Count the digits in a phone string.
export function phoneDigitCount(value) {
  return String(value || "").replace(/\D/g, "").length;
}

// True when the digits carry the default country code twice over a national number — the shape
// the old prefill produced. The LENGTH test is what makes this safe: with "65"/8 it matches
// exactly 12 digits beginning "6565", so the real landline 6565701000 (10 digits, "65" + the
// 8-digit national number 65701000) is not flagged. A prefix test alone would reject it.
export function isDoubledCountryCode(value) {
  const digits = phoneDigits(value);
  return (
    digits.startsWith(DEFAULT_COUNTRY_CODE + DEFAULT_COUNTRY_CODE) &&
    digits.length === 2 * DEFAULT_COUNTRY_CODE.length + NATIONAL_NUMBER_LENGTH
  );
}

/**
 * Gate a typed phone number before it can start a conversation. Replaces the old
 * "at least 7 digits" check, which let a doubled country code straight through.
 *
 * @param {string} value
 * @returns {{ ok: boolean, message: string }} message is "" when ok.
 */
export function validatePhoneInput(value) {
  const digits = phoneDigits(value);
  if (!digits) {
    return { ok: false, message: "Enter a phone number." };
  }
  if (isDoubledCountryCode(digits)) {
    return {
      ok: false,
      message: `That has ${DEFAULT_PHONE_PREFIX} twice — remove the extra country code.`,
    };
  }
  // A bare national number is fine: the backend prepends the default country code.
  if (digits.length === NATIONAL_NUMBER_LENGTH) {
    return { ok: true, message: "" };
  }
  if (digits.length < MIN_PHONE_DIGITS || digits.length > MAX_PHONE_DIGITS) {
    return {
      ok: false,
      message: `Enter the full number with country code, e.g. ${DEFAULT_PHONE_PREFIX}91234567.`,
    };
  }
  return { ok: true, message: "" };
}
